<script setup lang="ts">
  /**
   * Dialog 通用组件
   * @description
   * 1. 封装了 DialogRoot, DialogTrigger, DialogContent 等子组件
   * 2. 统一管理弹窗的头部、内容和底部操作区
   * 3. 通过属性配置标题和描述，或通过 slot 自定义
   */
  import {
    DialogRoot,
    type DialogRootEmits,
    type DialogRootProps,
    useForwardPropsEmits,
  } from 'reka-ui'
  import { reactiveOmit } from '@vueuse/core'
  import { cn } from '@rapoo/element/lib/utils'
  import { X } from 'lucide-vue-next'
  import { useSlots, type Slots } from 'vue'

  import DialogTrigger from './DialogTrigger.vue'
  import DialogContent from './DialogContent.vue'
  import DialogHeader from './DialogHeader.vue'
  import DialogTitle from './DialogTitle.vue'
  import DialogDescription from './DialogDescription.vue'
  import DialogFooter from './DialogFooter.vue'
  import DialogClose from './DialogClose.vue'
  import { Button } from '../button/index.ts'

  interface Props extends DialogRootProps {
    /**
     * 弹窗标题
     */
    title?: string
    /**
     * 弹窗描述文本
     */
    description?: string
    /**
     * 内容区域的自定义类名
     */
    contentClass?: string
    /**
     * 遮罩层的自定义类名
     */
    overlayClass?: string
    /**
     * 点击遮罩层是否关闭弹窗
     */
    closeOnOverlayClick?: boolean
    /** 关闭后是否将焦点恢复到打开 Dialog 前的元素。 */
    restoreFocus?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    closeOnOverlayClick: true,
    restoreFocus: true,
  })
  const emits = defineEmits<DialogRootEmits>()
  const dialogSlots: Slots = useSlots()

  const delegatedProps = reactiveOmit(
    props,
    'title',
    'description',
    'contentClass',
    'overlayClass',
    'closeOnOverlayClick',
    'restoreFocus',
  )
  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (!props.closeOnOverlayClick) {
      event.preventDefault()
    }
  }
</script>

<template>
  <DialogRoot v-bind="forwarded">
    <DialogTrigger v-if="dialogSlots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogContent
      :class="
        cn(
          'sm:max-w-106.25 border-none p-4 duration-300 rounded-5xl sm:rounded-5xl',
          props.contentClass,
        )
      "
      :close-on-overlay-click="props.closeOnOverlayClick"
      :overlay-class="cn('bg-mask20 duration-300', props.overlayClass)"
      :show-close="false"
      :restore-focus="props.restoreFocus"
      @escape-key-down="handleEscapeKeyDown"
    >
      <DialogHeader v-if="title || description || dialogSlots.header">
        <slot name="header">
          <DialogTitle
            v-if="title"
            class="text-2xl-medium font-medium text-black-white flex items-center justify-between"
            >{{ title }}
            <DialogClose
              class="text-black-white hover:text-text-active transition-colors flex items-center justify-center"
            >
              <Button color="text-b-w" variant="frameless" size="icon-sm" shape="circle">
                <X class="size-6" />
              </Button>
            </DialogClose>
          </DialogTitle>
        </slot>
      </DialogHeader>
      <DialogDescription v-if="description" class="text-text-glass-default">{{
        description
      }}</DialogDescription>

      <slot />

      <DialogFooter v-if="dialogSlots.footer">
        <slot name="footer" />
      </DialogFooter>
    </DialogContent>
  </DialogRoot>
</template>
